import type { TemplatePage } from '../templates';

const filled = [
  { item: 'Replace kitchen light fixture with customer-supplied pendant, same box, test', amount: '$120' },
  { item: 'Re-caulk master tub and shower surround, remove old silicone and mildew', amount: '$165' },
  { item: 'Install two exterior motion lights on existing boxes', amount: '$150' },
  { item: 'Trip charge, includes first half hour', amount: '$65' },
  { item: 'Optional: weatherstrip and new door sweep on the back door', amount: '$85' },
];

const faq = [
  {
    q: 'What is the best format for a handyman quote?',
    a: 'A task list: one line per job with a short description, who supplies the parts, and a price. Customers usually call with several small jobs, and a line for each lets them choose which to do now.',
  },
  {
    q: 'Should a handyman quote include a trip charge?',
    a: 'If you charge one, put it on the quote as its own line or in the terms, so it is agreed before you arrive. The template has a line for it with the amount left blank.',
  },
  {
    q: 'What happens when the customer adds jobs during the visit?',
    a: 'Quote the new tasks separately and get them agreed before you start them. In Scopie that means a new request for the same client with its own quote, so the first quote stays as signed.',
  },
  {
    q: 'Is the handyman template free?',
    a: 'Yes. Read it, download the PDF or copy it into your own paperwork. No sign-up required.',
  },
];

const page: TemplatePage = {
  slug: 'handyman-quote-template',
  path: '/templates/handyman-quote-template',
  primaryKeyword: 'handyman quote template',
  secondaryKeywords: ['handyman estimate template', 'handyman invoice template', 'home repair quote form'],
  title: 'Free Handyman Quote Template (PDF + Example) | Scopie', // 52
  metaDescription:
    'A free handyman quote template laid out as a task list: one line per repair, parts supplied by whom, trip charge, and hidden-condition terms. With example.', // 155
  h1: 'Handyman quote template',
  answerFirst:
    'A handyman quote works best as a task list: one line per repair with who supplies the parts and the price, then your trip charge or minimum and what happens if a job uncovers more work. This free template uses that layout, with a filled-in example of a four-task visit and an optional extra.',
  lastReviewed: '2026-10-07',
  schemaType: 'DigitalDocument',
  leadSheet: true,
  blocks: [
    {
      type: 'document',
      id: 'template',
      h2: 'The task-list quote',
      intro: 'Add one line per task; mark who supplies each part.',
      pdfName: 'handyman-quote-template',
      sections: [
        { heading: 'Your business', lines: ['[Name] · [Phone] · [Email] · [License or registration, if your state requires it]'] },
        { heading: 'Customer', lines: ['[Name] · [Address] · access notes [gate code / pets / parking]', 'Quote [number] · [date] · valid until [date]'] },
        {
          heading: 'Tasks',
          lines: [
            '[Task 1]: [description] · parts supplied by [me / customer] · [price]',
            '[Task 2]: [description] · parts supplied by [me / customer] · [price]',
            '[Task 3]: [description] · parts supplied by [me / customer] · [price]',
            'Trip charge or minimum: [amount and what it includes]',
            'Total for this visit: [amount]',
          ],
        },
        {
          heading: 'Terms',
          lines: [
            'If a task uncovers hidden damage (rot, failed valves, wiring), I stop, explain and quote it before continuing.',
            'Customer-supplied parts must be on site and complete; a wrong or missing part may mean a return visit at [your terms].',
            'Extra tasks requested on the day are quoted separately.',
            'Cancellation: [your terms].',
          ],
        },
        { heading: 'Agreed', lines: ['[Customer name] · [Signature] · [Date]'] },
      ],
    },
    {
      type: 'sample-quote',
      id: 'filled',
      h2: 'A four-task visit, filled in',
      label: 'Example: lights, caulk and a door',
      lines: filled,
      total: '$585',
      totalLabel: 'Total with the door weatherstripping',
      note: 'Example numbers, not a rate card.',
    },
    {
      type: 'list',
      id: 'tips',
      h2: 'Writing task lines customers understand',
      style: 'steps',
      items: [
        { title: 'Say what and where', text: '"Re-caulk master tub" beats "caulking". The customer knows exactly which job it is.' },
        { title: 'Note existing conditions', text: '"On existing box" or "same location" tells the customer you are not running new wiring or plumbing.' },
        { title: 'Separate parts from labor when the customer buys them', text: 'It avoids arguments about markup and about the wrong size.' },
        { title: 'Keep extras optional', text: 'Small add-ons like a door sweep are easy yeses when they sit on the same quote.' },
      ],
    },
    {
      type: 'callout',
      id: 'licensing',
      text: 'Some states limit the size or type of work a handyman can take on without a contractor license, and electrical or plumbing work often needs a licensed trade. Check the rules where you work before quoting those tasks.',
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Small-job quoting habits that cost handymen',
      style: 'mistakes',
      items: [
        { title: 'Quoting from memory on the doorstep', text: 'A number said out loud gets remembered lower than it was. Write it down before you start.' },
        { title: 'No line for disposal', text: 'Old fixtures, doors and drywall offcuts need to go somewhere; hauling them is time and fees.' },
        { title: 'Undercharging small tasks', text: 'A ten-minute job still needs the drive, the tools out and the cleanup. Your minimum exists for that.' },
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Handyman quote template questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/handyman', anchor: 'Scopie for handyman businesses', status: 'live' },
    { href: '/templates/painting-estimate-template', anchor: 'painting estimate template', status: 'live' },
    { href: '/templates', anchor: 'All free templates', status: 'live' },
  ],
  cta: {
    heading: 'Send the task list as a link',
    text: 'Save your common repairs and trip charge in Scopie, then email each handyman quote in a minute.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
